import React, { useState } from 'react';
import ImageUpload from '../../components/ImageUpload';
import './CreateStoreModal.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

const CreateStoreModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    password: '',
    deadline: '',
    logo_url: '',
    logo_initials: '',
    contact_email: '',
    description: '',
    pickup_type: 'store',
    completion_email_subject: '',
    completion_email_body: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const generateInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 4);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Auto-generate slug from name if not manually edited
      if (name === 'name' && !slugManuallyEdited) {
        updated.slug = generateSlug(value);
        updated.logo_initials = generateInitials(value);
      }

      return updated;
    });

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSlugChange = (e) => {
    setSlugManuallyEdited(true);
    handleChange(e);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/team-stores/admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          password: formData.password || null,
          deadline: formData.deadline || null,
          logo_url: formData.logo_url || null,
          logo_initials: formData.logo_initials || generateInitials(formData.name),
          contact_email: formData.contact_email || null,
          description: formData.description || null,
          pickup_type: formData.pickup_type || 'store',
          completion_email_subject: formData.completion_email_subject || null,
          completion_email_body: formData.completion_email_body || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(data.store);
        // Reset form
        setFormData({
          name: '',
          slug: '',
          password: '',
          deadline: '',
          logo_url: '',
          logo_initials: '',
          contact_email: '',
          description: '',
          pickup_type: 'store',
          completion_email_subject: '',
          completion_email_body: ''
        });
        setSlugManuallyEdited(false);
      } else {
        const error = await response.json();
        if (error.message?.includes('Slug already exists')) {
          setErrors({ slug: 'This slug is already in use. Please choose a different one.' });
        } else {
          setErrors({ submit: error.message || 'Error creating store' });
        }
      }
    } catch (error) {
      console.error('Error creating store:', error);
      setErrors({ submit: 'Error creating store. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      slug: '',
      password: '',
      deadline: '',
      logo_url: '',
      logo_initials: '',
      contact_email: '',
      description: '',
      pickup_type: 'store',
      completion_email_subject: '',
      completion_email_body: ''
    });
    setErrors({});
    setSlugManuallyEdited(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="tsm-modal-overlay" onClick={handleClose}>
      <div className="tsm-modal" onClick={e => e.stopPropagation()}>
        <div className="tsm-modal-header">
          <h2>Create New Team Store</h2>
          <button className="tsm-modal-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="tsm-modal-body">
          {errors.submit && (
            <div className="tsm-form-error-banner">{errors.submit}</div>
          )}

          <div className="tsm-form-section">
            <h3>Basic Information</h3>

            <div className="tsm-form-group">
              <label htmlFor="name">Team Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Kerrville Tigers"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="tsm-field-error">{errors.name}</span>}
            </div>

            <div className="tsm-form-group">
              <label htmlFor="slug">Store URL Slug *</label>
              <div className="tsm-slug-preview">
                <span className="tsm-slug-prefix">fitness1stsports.com/?team_store=</span>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  placeholder="kerrville-tigers"
                  className={errors.slug ? 'error' : ''}
                />
              </div>
              {errors.slug && <span className="tsm-field-error">{errors.slug}</span>}
            </div>

            <div className="tsm-form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="A brief description of this team store..."
                rows={2}
              />
            </div>
          </div>

          <div className="tsm-form-section">
            <h3>Store Settings</h3>

            <div className="tsm-form-row">
              <div className="tsm-form-group">
                <label htmlFor="password">Password Protection (Optional)</label>
                <input
                  type="text"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank for no password"
                />
                <span className="tsm-field-hint">Customers will need this password to access the store</span>
              </div>

              <div className="tsm-form-group">
                <label htmlFor="deadline">Order Deadline (Optional)</label>
                <input
                  type="datetime-local"
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleChange}
                />
                <span className="tsm-field-hint">Store will auto-close after this date</span>
              </div>
            </div>

            <div className="tsm-form-group">
              <label htmlFor="contact_email">Contact Email (Optional)</label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="team@example.com"
                className={errors.contact_email ? 'error' : ''}
              />
              {errors.contact_email && <span className="tsm-field-error">{errors.contact_email}</span>}
            </div>
          </div>

          <div className="tsm-form-section">
            <h3>Branding</h3>

            <div className="tsm-form-row tsm-form-row-branding">
              <div className="tsm-form-group tsm-form-group-logo">
                <ImageUpload
                  label="Logo (Optional)"
                  value={formData.logo_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
                  placeholder="Click or drag logo here"
                />
              </div>

              <div className="tsm-form-group">
                <label htmlFor="logo_initials">Logo Initials</label>
                <input
                  type="text"
                  id="logo_initials"
                  name="logo_initials"
                  value={formData.logo_initials}
                  onChange={handleChange}
                  placeholder="KT"
                  maxLength={4}
                />
                <span className="tsm-field-hint">Used if no logo image is uploaded</span>

                {formData.logo_initials && !formData.logo_url && (
                  <div className="tsm-logo-preview">
                    <span className="tsm-preview-label">Preview:</span>
                    <div className="tsm-initials-preview">{formData.logo_initials}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="tsm-form-section">
            <h3>Order Completion</h3>
            <p className="tsm-section-desc">Configure how customers are notified when their orders are ready.</p>

            <div className="tsm-form-group">
              <label>Pickup Type</label>
              <div className="tsm-radio-group">
                <label className="tsm-radio-option">
                  <input
                    type="radio"
                    name="pickup_type"
                    value="store"
                    checked={formData.pickup_type === 'store'}
                    onChange={handleChange}
                  />
                  <span className="tsm-radio-label">
                    <strong>Store Pickup</strong>
                    <small>Customer picks up at Fitness First Sports</small>
                  </span>
                </label>
                <label className="tsm-radio-option">
                  <input
                    type="radio"
                    name="pickup_type"
                    value="coach"
                    checked={formData.pickup_type === 'coach'}
                    onChange={handleChange}
                  />
                  <span className="tsm-radio-label">
                    <strong>Coach/Parent Pickup</strong>
                    <small>Coach or team parent distributes to customers</small>
                  </span>
                </label>
              </div>
            </div>

            <div className="tsm-form-group">
              <label htmlFor="completion_email_subject">
                Custom Email Subject <span className="tsm-optional">(optional)</span>
              </label>
              <input
                type="text"
                id="completion_email_subject"
                name="completion_email_subject"
                value={formData.completion_email_subject}
                onChange={handleChange}
                placeholder={formData.pickup_type === 'store'
                  ? 'Your order is ready for pickup!'
                  : 'Your order has been delivered to your coach!'}
              />
              <span className="tsm-field-hint">Leave blank to use default based on pickup type</span>
            </div>

            <div className="tsm-form-group">
              <label htmlFor="completion_email_body">
                Custom Email Message <span className="tsm-optional">(optional)</span>
              </label>
              <textarea
                id="completion_email_body"
                name="completion_email_body"
                value={formData.completion_email_body}
                onChange={handleChange}
                rows={4}
                placeholder={formData.pickup_type === 'store'
                  ? 'Your order is ready for pickup at Fitness First Sports!'
                  : 'Your order has been delivered to your coach/team parent for distribution.'}
              />
              <span className="tsm-field-hint">Leave blank to use default message</span>
            </div>
          </div>

          <div className="tsm-modal-footer">
            <button type="button" className="tsm-btn tsm-btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="tsm-btn tsm-btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Store'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateStoreModal;
